import { useMemo } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { ThemePreview } from './ThemePreview'

import { useCreateDocumentTheme } from '@/services/queries/document_themes'
import { useDocumentState } from '@/state'
import { useOperationPageTemplateAttributes } from '../../OperationDocument/hooks/useOperationPageTemplateAttributes'

import type { TemplateAttribute } from '@/lib/api/client'

const formSchema = z.object({
  themename: z.string().min(2, {
    message: 'Template name must be at least 2 characters.',
  }),
})

export const CreateThemeForm = ({
  onCreate,
  onCancel,
}: {
  onCreate: () => void
  onCancel: () => void
}) => {
  const documentPageId = useDocumentState((state) => state.currentPageInView)

  const operationPageTemplateAttributes = useOperationPageTemplateAttributes({
    documentPageId,
  })

  const previewTemplateAttributes = useMemo(
    () =>
      Object.values(operationPageTemplateAttributes)
        .flatMap((x) => x as TemplateAttribute)
        .filter((x) => !!x)
        .filter(
          (x) =>
            !x.template_values?.subPageId ||
            x.template_values?.subPageId === documentPageId,
        ),
    [operationPageTemplateAttributes],
  )

  const { mutate: createDocumentTheme, isPending: isCreatingTheme } =
    useCreateDocumentTheme({
      onCreate,
    })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      themename: '',
    },
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    if (documentPageId) {
      createDocumentTheme({
        name: values.themename,
        theme_type: 'operation',
        documentPageId,
      })
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="w-full max-w-96 flex flex-col space-y-4 justify-self-center">
          <FormField
            control={form.control}
            name="themename"
            render={({ field }) => (
              <FormItem className="z-[99]">
                <FormLabel>Template Name</FormLabel>
                <FormControl>
                  <Input className="rounded-full z-50" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div
            className="w-full max-w-64 flex self-center justify-self-center items-center"
            style={{
              transform: 'scale(1.5)',
              minHeight: 295,
            }}
          >
            <ThemePreview
              themeTemplateAttributes={previewTemplateAttributes}
              hideControls
              isReadOnly
            />
          </div>
        </div>
        <div className="flex items-center space-x-4 justify-self-end z-[99] relative">
          <Button
            type="button"
            variant="ghost"
            className="!text-sm text-gray-500 rounded-full"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isCreatingTheme}
            className="!text-sm bg-primary-20 text-primary-50 rounded-full shadow-none"
          >
            {isCreatingTheme ? (
              <div className="flex items-center space-x-2">
                <Spinner />
                <span>Saving</span>
              </div>
            ) : (
              <span>Save Template</span>
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}
